import {
  Button,
  Card,
  Col,
  Form,
  Grid,
  Input,
  Radio,
  Row,
  Select,
  Typography
} from "antd";
import { useEffect, useRef, useState } from "react";

import {
  AddressControl,
  ContactField,
  localityEnum,
  openNotification
} from "@seims/shared";

import { areaCodes } from "../../data/Location";
import { Messages, Notify } from "../../services/custom/Messages";
import lookupApi from "../../services/partnershipLookupService";
import { util } from "../../utils/functions/custom";
import { recordError } from "../../utils/logService";

const { Title } = Typography;

export const OrganizationForm = ({
  formRef,
  formData,
  onShowCreate,
  onSaveOrganization,
  api,
  resetFormData = () => {},
  loadDetails = false,
  onCancelForm
}) => {  
  const [types, setTypes] = useState([]);
  const [contributions, setContributions] = useState([]);
  const [localityOptions, setLocalityOption] = useState([]);
  const [defaultContact, setDefaultContact] = useState("+27");
  const [address, setAddress] = useState();
  const [partnerSummary, setpartnerSummary] = useState({
    name: "",
    contact: "",
  });
  const [isLocal, setIsLocal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showAddressInfo, setshowAddressInfo] = useState(false);
  const [showOther, setShowOther] = useState(false);

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;


  const formItemLayout = {
    labelCol: {
      span: 12,
    },
    wrapperCol: {
      span: 12,
    },
  };

  const [form] = Form.useForm();
  const formName = "PartnerForm";
  const emailAddressRef = useRef(null);
  const editablePartnerLoaded = useRef(false);

  const isEditMode = formData?.id > 0;
  


  const getPartnerTypes = async () => {
    try {
      const response = await lookupApi.getPartnerTypes();
      if (response) {
        const { data } = response;
        setTypes(data);
      }
    } catch (error) {
      recordError(error);
    }
  };

  const getLocalityTypes = async () => {
    try {
      const response = await lookupApi.getLocalityTypes();
      if (response) {
        const { data } = response;
        setLocalityOption(data.sort(util.setDynamicSort("key")));
      }
    } catch (error) {
      recordError(error);
    }
  };

  const getContributions = async () => {
    try {
      const response = await lookupApi.getContributions();
      if (response) {
        const { data } = response;
        setContributions(data);
      }
    } catch (error) {
      recordError(error);
    }
  };

  const getLocality = (country = "South Africa", selectedId = null) => {
    const localityType = country == "South Africa" ? localityEnum.LOCAL : localityEnum.FOREIGN
        const findCallback = (record) => {
          return selectedId === null
            ? [record.value, record.key].includes(localityType)
            : Number(record.key) === Number(selectedId)
        }

        const selectedLocality = localityOptions.find(findCallback);

        return selectedLocality
  }

  const onChangeLocality = (e) => {
    const selected = e.target.value;
    const selectedLocality = getLocality(null, selected);
    const currentContact = form.getFieldValue("contact") ?? defaultContact;

    if (
      selectedLocality?.value === localityEnum.FOREIGN &&
      String(currentContact).trim().startsWith("+27")
    ) {
      openNotification(
        "Address validation",
        "Foreign addresses should not use a +27 contact number.",
        Notify.WARNING
      );
    }

    setIsLocal(selectedLocality?.value === localityEnum.LOCAL);
    setShowModal(true);
  };

  const handleCancel = () => {
    setShowModal(false);
  };

  const handleContactChange = (value) => {
    
    if (value === 0 || value.length < 10) {
      form.setFieldsValue({ contact: undefined });
      form.setFieldsValue({ contact: util.toNumeric(value) });
      return;
    }
    form.setFieldsValue({ contact: value });
    let partner = { ...partnerSummary };
    partner.contact = value;
    setpartnerSummary(partner);
    
  };

  const onNameBlur = (e) => {
    const { value, name } = e.target;
    const textValue = util.toPascalText(value);

    form.setFieldsValue({ [name]: textValue });
    let partner = { ...partnerSummary };
    partner.name = textValue;
    setpartnerSummary(partner);
  };

  const handleModalSubmit = (values) => {
    const _address = {
      ...values,
      country: values.country?.value ?? values.country,
      ...(isLocal && { province: values?.province?.value ?? values?.province }),
    };

    setAddress(_address);
    setshowAddressInfo(true);
    setShowModal(false);
  };

  const handleSubmit = () => {
    try {
      addOrganization();
    } catch (error) {
      console.log(">>> ERROR", error);
      openNotification("Error", "An unusual error has happened", Notify.ERROR);
      recordError(error);
      console.error(
        `THERE ARE ${error?.errorFields?.length} REQUIRED FIELDS WITHOUT VALUES`
      );
    }
  };

  const addOrganization = async () => {
    var msg;
    var partnerReq = form.getFieldsValue();
    var action = `${isEditMode ? "Update" : "Adding"} Partner`;
    
    
    try {

      const objContributions =  typeof partnerReq?.contributions[0] === "object"
      const partnerContributions = objContributions ?
            partnerReq?.contributions?.map((c) =>  c.key) :
            contributions.filter((c) => 
              partnerReq?.contributions?.includes(String(c.key)) || 
              partnerReq?.contributions?.includes(String(c.value))
            ).map((c) => c.key);

      const partnerType = typeof partnerReq?.partnerType === "object" ?
                                                    partnerReq?.partnerType :
                                                    types.find((t) => Number(t.key) === Number(partnerReq.partnerType));

      const partnerPayload = {
        ...partnerReq,
        contributions: partnerContributions,
        partnerType: partnerType,
      };
      
      const request = await api.addPartnership(partnerPayload, address);

      if (request?.status == 200 || request?.status == 201) {
        const { id } = request?.data;
        const { key, value } = partnerReq?.partnerType;
        const filteredContr = contributions?.filter((c) =>
          partnerReq?.contributions.includes(c.key.toString())
        );
        let modifiedContributions;
        //Remove unused object attributes
        delete partnerReq["contributions"];
        delete partnerReq["partnerType"];

        if (filteredContr.length > 0) {
          modifiedContributions = filteredContr.map((c) => ({
            label: c.value.toString(),
            value: c.key.toString(),
          }));
        }

        const organizationDetails = {
          ...partnerReq,
          id: id,
          contributions: modifiedContributions,
          type: value,
          typeid: key,
          // otherContributionss: partnerReq.otherContribution,
        };

        onSaveOrganization(organizationDetails);
        // setCreateData(formData);
        msg = util.parseMessage( isEditMode ? Messages.UPDATE_SUCCESS : Messages.ADD_SUCCESS, "Partner");
        openNotification(action, msg, Notify.SUCCESS);
        onCancel();
        return true;
      }

      msg = util.parseMessage(Messages.ADD_FAILURE, "Partner");
      openNotification(action, msg, Notify.ERROR);
      return false;
    } catch (error) {
      if (error?.response?.status == 409) {
        msg = util.parseMessage(Messages.EXIST_ADD_FAILURE, "Partner");
        openNotification(action, msg, Notify.ERROR);
        recordError(error);

        return false;
      }

      openNotification(action, "An unusual error has happened while retrieving partner details", Notify.ERROR);
      recordError(error);
      return false;
    }
  };


  const getAreaCodeByCountryName = (areaCodes, countryName) => {
    return areaCodes.find(item => item.countryName === countryName);
  };

  const getPartnerDetailsToEdit = async (id) => {
    await api.getPartnerDetails(id).then((response) => {
      if (response?.status === 200) {
        const { data } = response;
        

        const selectedLocality = getLocality(data?.address?.country);
        
        const areaCode = getAreaCodeByCountryName(areaCodes, data?.address?.country);
        let contact = data?.telephoneNumber;
        
        if (!(data?.telephoneNumber !== null && `${data?.telephoneNumber}`.includes("+"))) {
          contact = `${areaCode.value} ${data?.telephoneNumber}`;
        }
        
        console.log("C", contact, areaCode);
        
        const partnerDetails = {
          ...data,
          address: selectedLocality.key,
          contact: contact,
          partnerType: data?.partnerType ?? undefined,
        };

        setIsLocal(selectedLocality.value === localityEnum.LOCAL);
        setshowAddressInfo(true);
        
        form.setFieldsValue(partnerDetails);
        
        setDefaultContact(partnerDetails.contact)
        setAddress(data.address);
        onShowCreate(true);
      } else {
        openNotification("Get Partner", "Failed to retrieve partner details", Notify.ERROR);
      }
    }).catch((error) => {
      openNotification("Get Partner", "An unusual error has happened while retrieving partner details", Notify.ERROR);
      recordError(error);
    });
  }

  const clearFormContents = () => {
    resetFormData({});
    form.resetFields();
    setAddress({});
    setpartnerSummary({});
    setShowModal(false);
    setshowAddressInfo(false);
    setShowOther(false);
  };

  const onCancel = () => {
    clearFormContents();
    // tells parent to go back to search + highlight
    if (onCancelForm) {
      onCancelForm();
    } else {
      onShowCreate(false);
    }
  };

  const checkEmailExistance = async () => {
    const entity = "Email";
    let msg;
    const inputCheck = form.getFieldValue("email");

    try {
      if (inputCheck) {
        const emailValidity = await api.checkEmailExists(inputCheck);
        if (emailValidity.status === 204) {
          return;
        }
      }
    } catch (error) {
      if (error?.response?.status === 409) {
        msg = util.parseMessage(Messages.USER_EXISTS, entity);
        openNotification("Email validation", msg, Notify.WARNING);

        //  SAFE focus
        emailAddressRef.current?.focus();
        return;
      } else {
        msg = util.parseMessage(Messages.INVALID_EMAIL_DOMAIN, entity);
        openNotification("Email validation", msg, Notify.ERROR);

        //  SAFE focus
        emailAddressRef.current?.focus();
        return;
      }
    }
  };

  useEffect(() => {
    getPartnerTypes();
    getLocalityTypes();
    getContributions();

    return () => {
      onCancel();
    };
  }, []);
  
  useEffect(() => {
    
    if (loadDetails && !editablePartnerLoaded.current && localityOptions?.length > 0) {
      getPartnerDetailsToEdit(formData?.id);
      editablePartnerLoaded.current = true;
    }
  }, [formData, loadDetails, localityOptions]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();   // validate all fields
      form.submit();                                // triggers onFinish, no page reload
    } catch (errorInfo) {
      // validation failed – do nothing, Ant Design shows field errors
    }
  };


  return (
  <>
    <Form
      {...formItemLayout}
      name={formName}
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      ref={formRef}
      initialValues={formData}
    >
      <Form.Item hidden name="id">
        <Input />
      </Form.Item>

      {/* ---- Heading ---- */}
      {/* ---- Row 1: Name + Acronym + Contact + Email ---- */}
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item
            name="name"
            label="Organisation Name"
            rules={[{ required: true, message: "Please enter organisation name" }]}
          >
            <Input name="name" onBlur={onNameBlur} />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item name="acronym" label="Organisation Name (Acronym)">
            <Input name="acronym" onBlur={onNameBlur} />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item
            name="contact"
            label="Contact"
            rules={[
              { required: true, message: "Please enter organisation contact" },
              { min: 10, message: "Contact length expects 10 numeric values" },
            ]}
          >
            <div style={{ width: "100%" }}>
              <ContactField
                onContactChange={handleContactChange}
                contactValue={defaultContact}
              />
            </div>
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item
            name="email"
            label="Organisation Email"
            rules={[
              { required: true, type: "email", message: "Please enter organisation Email" },
            ]}
          >
            <Input
              ref={emailAddressRef}
              onBlur={async () => {
                await checkEmailExistance();
                form.validateFields(["email"]);
              }}
              type="email"
            />
          </Form.Item>
        </Col>
      </Row>

      {/* ---- Row 2: Contributions + Partner Type ---- */}
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item
            name="contributions"
            label="Contributions"
            rules={[
              { required: true, message: "Please select at least 1 contribution type" },
            ]}
          >
            <Select mode="multiple" placeholder="Select contributions">
              {contributions.map((c) => (
                <Select.Option key={c.key} value={String(c.key)}>
                  {c.value}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item
            name="partnerType"
            label="Organisation Type"
            rules={[{ required: true, message: "Please select organisation type" }]}
          >
            <Select placeholder="Select type">
              {types.map((t) => (
                <Select.Option key={t.key} value={t.key}>
                  {t.value}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      {/* ---- Row 3: Address Option ---- */}
      <Row gutter={16}>
        <Col xs={24}>
          <Form.Item
            name="address"
            label="Address Option"
            rules={[
              { required: true, message: "Please select organisation address option" },
            ]}
          >
            <Radio.Group onChange={onChangeLocality} options={localityOptions.map((option) => ({ label: option.value, value: option.key}))}/>
          </Form.Item>
        </Col>
      </Row>

      {/* ---- Row 4: Address preview card (if available) ---- */}
      {showAddressInfo && (
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Card
              style={{
                marginTop: 16,
                backgroundColor: "#fafafa",
                borderColor: "#ccc",
              }}
              actions={[
                <Button key="edit" type="default" onClick={() => setShowModal(true)}>
                  Edit
                </Button>,
              ]}
            >
              {address && (
                <Card.Meta
                  title={isLocal ? "Local Address" : "Foreign Address"}
                  description={`${address.street} ${address.cityOrTown} ${address.postalCode}, ${address.country}`}
                />
              )}
            </Card>
          </Col>
        </Row>
      )}

      {/* ---- Actions ---- */}
      <Row justify="end" style={{ marginTop: 20 }}>
        <Col>
          <Button onClick={onCancel} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button type="primary" htmlType="button" onClick={handleSave}>
            Save
          </Button>
        </Col>
      </Row>
    </Form>

    <AddressControl
      key="2"
      onCancel={handleCancel}
      visible={showModal}
      title="Add Address"
      isLocal={isLocal}
      onModalSubmit={handleModalSubmit}
      address={address}
    />
  </>
);
};
